import { ScopedKey, ServerScope, type ServerScope as ServerScopeType } from "@/utils/server-scope"

const key = (scope: ServerScopeType, directory: string, sessionID: string) => ScopedKey.from(scope, directory, sessionID)

export const SESSION_PREFETCH_TTL = 15_000

type Meta = {
  limit: number
  cursor?: string
  complete: boolean
  at: number
}

export function shouldSkipSessionPrefetch(input: { message: boolean; info?: Meta; chunk: number; now?: number }) {
  if (input.message) {
    if (!input.info) return true
    if (input.info.complete) return true
    if (input.info.limit > input.chunk) return true
  } else {
    if (!input.info) return false
  }

  return (input.now ?? Date.now()) - input.info.at < SESSION_PREFETCH_TTL
}

const cache = new Map<string, Meta>()
const inflight = new Map<string, Promise<Meta | undefined>>()
const rev = new Map<string, number>()

const version = (id: string) => rev.get(id) ?? 0
const scopedArgs = (first: ServerScopeType | string, second?: string, third?: string) => ({
  scope: third ? (first as ServerScopeType) : ServerScope.local,
  directory: third ? second! : (first as string),
  sessionID: third ?? second!,
})

export function getSessionPrefetch(scope: ServerScopeType, directory: string, sessionID: string): Meta | undefined
export function getSessionPrefetch(directory: string, sessionID: string): Meta | undefined
export function getSessionPrefetch(first: ServerScopeType | string, second: string, third?: string) {
  const args = scopedArgs(first, second, third)
  return cache.get(key(args.scope, args.directory, args.sessionID))
}

export function getSessionPrefetchPromise(
  scope: ServerScopeType,
  directory: string,
  sessionID: string,
): Promise<Meta | undefined> | undefined
export function getSessionPrefetchPromise(directory: string, sessionID: string): Promise<Meta | undefined> | undefined
export function getSessionPrefetchPromise(first: ServerScopeType | string, second: string, third?: string) {
  const args = scopedArgs(first, second, third)
  return inflight.get(key(args.scope, args.directory, args.sessionID))
}

export function clearSessionPrefetchInflight(scope: ServerScopeType = ServerScope.local) {
  const prefix = ScopedKey.prefix(scope)
  for (const id of inflight.keys()) {
    if (id.startsWith(prefix)) inflight.delete(id)
  }
}

export function isSessionPrefetchCurrent(
  scope: ServerScopeType,
  directory: string,
  sessionID: string,
  value: number,
): boolean
export function isSessionPrefetchCurrent(directory: string, sessionID: string, value: number): boolean
export function isSessionPrefetchCurrent(
  first: ServerScopeType | string,
  second: string,
  third: string | number,
  fourth?: number,
) {
  if (fourth === undefined) return version(key(ServerScope.local, first as string, second)) === third
  return version(key(first as ServerScopeType, second, third as string)) === fourth
}

export function runSessionPrefetch(input: {
  directory: string
  scope?: ServerScopeType
  sessionID: string
  task: (value: number) => Promise<Meta | undefined>
}) {
  const id = key(input.scope ?? ServerScope.local, input.directory, input.sessionID)
  const pending = inflight.get(id)
  if (pending) return pending

  const value = version(id)

  const promise = input.task(value).finally(() => {
    if (inflight.get(id) === promise) inflight.delete(id)
  })

  inflight.set(id, promise)
  return promise
}

export function setSessionPrefetch(input: {
  directory: string
  scope?: ServerScopeType
  sessionID: string
  limit: number
  cursor?: string
  complete: boolean
  at?: number
}) {
  cache.set(key(input.scope ?? ServerScope.local, input.directory, input.sessionID), {
    limit: input.limit,
    cursor: input.cursor,
    complete: input.complete,
    at: input.at ?? Date.now(),
  })
}

export function clearSessionPrefetch(scope: ServerScopeType, directory: string, sessionIDs: Iterable<string>): void
export function clearSessionPrefetch(directory: string, sessionIDs: Iterable<string>): void
export function clearSessionPrefetch(
  first: ServerScopeType | string,
  second: string | Iterable<string>,
  third?: Iterable<string>,
) {
  const scope = third ? (first as ServerScopeType) : ServerScope.local
  const directory = third ? (second as string) : (first as string)
  const sessionIDs = third ?? (second as Iterable<string>)
  for (const sessionID of sessionIDs) {
    if (!sessionID) continue
    const id = key(scope, directory, sessionID)
    rev.set(id, version(id) + 1)
    cache.delete(id)
    inflight.delete(id)
  }
}

export function clearSessionPrefetchDirectory(scope: ServerScopeType, directory: string): void
export function clearSessionPrefetchDirectory(directory: string): void
export function clearSessionPrefetchDirectory(first: ServerScopeType | string, second?: string) {
  const scope = second ? (first as ServerScopeType) : ServerScope.local
  const directory = second ?? (first as string)
  const prefix = ScopedKey.prefix(scope, directory)
  const keys = new Set([...cache.keys(), ...inflight.keys()])
  for (const id of keys) {
    if (!id.startsWith(prefix)) continue
    rev.set(id, version(id) + 1)
    cache.delete(id)
    inflight.delete(id)
  }
}
